import React, { createContext, useContext, useReducer } from 'react';
import { cloneDeep, isEqual } from 'lodash';
import { recalcRow, stampSampleFlags } from './measurement-calc';
import { alignGradingsToSizes, unionSizeNames } from './size-ranges';
import type { MeasurementProduct, MeasurementsAction, MeasurementsPhase, MeasurementsState, SpecGrading, SpecPomItem } from './types';

/** Stable identity for a POM row within a product (matches the grid's row id). */
export const rowKeyOf = (item: SpecPomItem, index: number): string => item._id ?? item.code ?? `idx-${index}`;

/** Max distinct POM rows a user may edit before they must save (BM-9210 — hard cap). */
export const EDIT_ROW_CAP = 100;

/** Global row id joining a product to one of its POM rows (matches the grid's row id). */
const rowGlobalId = (devOrderId: string, rowKey: string): string => `${devOrderId}::${rowKey}`;

/**
 * Prepare freshly-loaded products for the grid, in order: give every POM row a grading entry for every
 * size column (so a size the product never graded still takes part), resolve the row's anchor size, then
 * run the calculation once so those newly-created sizes hold a derived measurement straight away rather
 * than staying blank until the row is touched. The pristine row is its own baseline, so seeding the
 * values marks nothing as user-changed.
 */
function prepareProducts(products: MeasurementProduct[], sizeNames: string[], gradingFormat: string): MeasurementProduct[] {
  return products.map((product) => ({
    ...product,
    specPomLibraries: (product.specPomLibraries ?? []).map((item) => {
      const prepared = stampSampleFlags(alignGradingsToSizes(item, sizeNames), product.sampleSizeName);
      return recalcRow(prepared, prepared, gradingFormat);
    }),
  }));
}

/** Change-relevant projection of a POM row (ignores UI-only flags) for value-diffing against the baseline. */
function normRow(item?: SpecPomItem): Record<string, unknown> {
  return {
    code: String(item?.code ?? '').trim(),
    description: String(item?.description ?? '').trim(),
    tolMinus: String(item?.tolMinus ?? '').trim(),
    tolPlus: String(item?.tolPlus ?? '').trim(),
    gradings: (item?.gradings ?? []).map((grading) => ({
      sizeName: grading.sizeName,
      measurment: String(grading.measurment ?? '').trim(),
      grading: String(grading.grading ?? '').trim(),
    })),
  };
}

/** True when a row's editable values differ from its pristine original (tolerance + standalone gradings included). */
function isRowChanged(item?: SpecPomItem, original?: SpecPomItem): boolean {
  return !isEqual(normRow(item), normRow(original));
}

/** Toggle membership of a value in a string list. */
function toggleInList(list: string[], value: string): string[] {
  return list.includes(value) ? list.filter((entry) => entry !== value) : [...list, value];
}

/** True if any product carries an unresolved user/supplier change flag (drives the amber clear button). */
function hasHighlights(products: MeasurementProduct[]): boolean {
  return products.some((product) =>
    (product.specPomLibraries ?? []).some(
      (item) =>
        item.isUserCodeChanged ||
        item.isUserDescChanged ||
        item.isSupplierCodeChanged ||
        item.isSupplierDescChanged ||
        (item.gradings ?? []).some((grading) => grading.isUserChanged || grading.isSupplierChanged),
    ),
  );
}

const initialState: MeasurementsState = {
  phase: 'validating',
  gradingGroupId: null,
  gradingGroupName: '',
  conversionType: '',
  gradingFormat: '',
  sizeRanges: [],
  eligibleProductIds: [],
  ineligibleProducts: [],
  products: [],
  originalProducts: [],
  supportingDocs: {},
  activeGrading: false,
  highlightActive: false,
  dirty: false,
  error: null,
  collapsedProducts: [],
  collapseAllActive: false,
  selectedRowIds: [],
  sourceDevOrderId: null,
  pushArmed: false,
  selectAllActive: false,
  loadingMore: false,
  changedRowIds: [],
  invalidCells: [],
  showEditCapWarning: false,
  scrollToInvalid: null,
};

/** devOrderId prefix of a grid row id (`devOrderId::rowKey`). */
const ROW_ID_SEPARATOR = '::';
const productIdOfRow = (rowId: string): string => {
  const at = rowId.indexOf(ROW_ID_SEPARATOR);
  return at === -1 ? rowId : rowId.slice(0, at);
};

/**
 * The push source implied by the current selection: a selection confined to one product names that
 * product, a selection spanning several names none. Derived on every change rather than latched on the
 * first tick, so the un-armed phase stays a plain multi-product bulk selection — nothing locked, nothing
 * blue — and Push simply switches off while more than one product is ticked.
 */
function deriveSourceDevOrderId(selectedRowIds: string[]): string | null {
  const productIds = new Set(selectedRowIds.map(productIdOfRow));
  return productIds.size === 1 ? [...productIds][0]! : null;
}

/**
 * POM codes are matched exactly (BM-9210): a code that differs by case or padding is a different POM.
 * Duplicate codes within one product are rejected by save validation, so the first match is unambiguous.
 */
function isSameCode(left: string | undefined, right: string | undefined): boolean {
  return Boolean(left) && left === right;
}

/** Every POM row id belonging to the given products. */
function pomRowIdsOf(products: MeasurementProduct[]): string[] {
  return products.flatMap((product) =>
    (product.specPomLibraries ?? []).map((item, index) => rowGlobalId(product.devOrderId, rowKeyOf(item, index))),
  );
}

/**
 * Every POM row's global id, across every product and regardless of collapse. Select-all works on the
 * whole table, not just the rows currently on screen — deriving it from the visible grid rows instead
 * would make a collapsed product look fully selected once its first row was ticked.
 */
export function allPomRowIds(state: MeasurementsState): string[] {
  return pomRowIdsOf(state.products);
}

/**
 * Each product's parent row id — the first POM row, which is where the grid draws the product-level
 * checkbox. A product with no POM rows has no parent checkbox, so it is skipped rather than given an id
 * the grid would never render.
 */
function parentRowIdsOf(products: MeasurementProduct[], excludeDevOrderId: string | null): string[] {
  return products.flatMap((product) => {
    if (product.devOrderId === excludeDevOrderId) return [];
    const firstItem = (product.specPomLibraries ?? [])[0];
    return firstItem ? [rowGlobalId(product.devOrderId, rowKeyOf(firstItem, 0))] : [];
  });
}

/** True while an armed push is waiting for target products — the state that makes select-all mean "tick every target". */
export function isPushTargeting(state: MeasurementsState): boolean {
  return Boolean(state.pushArmed && state.sourceDevOrderId);
}

/**
 * The rows the header checkbox-all acts on right now (BM-9210). While a push is armed it ticks only the
 * other products' parent rows — the source is already ticked and is what the push copies FROM, so
 * including it would be meaningless. With no push in progress it stays a plain select-every-row.
 */
export function selectAllRowIds(state: MeasurementsState): string[] {
  return isPushTargeting(state) ? parentRowIdsOf(state.products, state.sourceDevOrderId) : allPomRowIds(state);
}

/**
 * Phase to enter once a cohort is settled. A cohort with no common grading group has nothing to fetch, so
 * it goes straight to the terminal `empty` phase; entering `loading` would strand the module on a spinner
 * that no request will ever resolve.
 */
function cohortPhase(gradingGroupId: string | null, eligibleProductIds: string[]): MeasurementsPhase {
  return gradingGroupId && eligibleProductIds.length > 0 ? 'loading' : 'empty';
}

/**
 * Whether the push interaction lets a row's checkbox be changed right now (BM-9210):
 *  - not armed — nothing is locked, whichever product a tick lands on. Ticking one product must not
 *    grey out the rest: until Push is pressed this is an ordinary bulk selection that may span products;
 *  - armed — the source is locked in and only another product's parent row may be chosen as a target.
 */
export function isSelectDisabled(state: MeasurementsState, devOrderId: string, isFirstOfProduct: boolean): boolean {
  if (!isPushTargeting(state)) return false;
  return state.sourceDevOrderId === devOrderId || !isFirstOfProduct;
}

/** The source product's selected POM rows — the rows whose values a push copies out. */
export function selectedSourceRows(state: MeasurementsState, source: MeasurementProduct): SpecPomItem[] {
  const selected = new Set(state.selectedRowIds);
  return (source.specPomLibraries ?? []).filter((item, index) =>
    selected.has(rowGlobalId(source.devOrderId, rowKeyOf(item, index))),
  );
}

/** Products selected as push targets — every selected product other than the source. */
export function targetProductIds(state: MeasurementsState): Set<string> {
  const ids = new Set(state.selectedRowIds.map(productIdOfRow));
  if (state.sourceDevOrderId) ids.delete(state.sourceDevOrderId);
  return ids;
}

/**
 * Overlay the source row's size values onto the target row's, matched by size name: only `measurment`
 * and `grading` move across, and only for sizes the target actually carries. Each overwritten size is
 * flagged `isUserChanged` so it turns amber and Clear-highlights can undo it. Pure.
 */
function copyGradingValues(sourceGradings: SpecGrading[], targetGradings: SpecGrading[]): SpecGrading[] {
  const bySizeName = new Map(sourceGradings.map((grading) => [grading.sizeName, grading]));
  return targetGradings.map((grading) => {
    const from = bySizeName.get(grading.sizeName);
    if (!from) return grading;
    return { ...grading, measurment: from.measurment ?? '', grading: from.grading ?? '', isUserChanged: true };
  });
}

/** The pristine (as-loaded) POM row for a product, used for amber (change) diffs. */
function getOriginalItem(state: MeasurementsState, devOrderId: string, rowId: string): SpecPomItem | undefined {
  const product = state.originalProducts.find((entry) => entry.devOrderId === devOrderId);
  if (!product) return undefined;
  return (product.specPomLibraries ?? []).find((item, index) => rowKeyOf(item, index) === rowId);
}

function clearItemFlags(item: SpecPomItem): SpecPomItem {
  return {
    ...item,
    isUserCodeChanged: false,
    isUserDescChanged: false,
    isSupplierCodeChanged: false,
    isSupplierDescChanged: false,
    gradings: (item.gradings ?? []).map((grading) => ({ ...grading, isUserChanged: false, isSupplierChanged: false })),
  };
}

function updateProductItem(
  state: MeasurementsState,
  devOrderId: string,
  rowId: string,
  updateItem: (item: SpecPomItem) => SpecPomItem,
): MeasurementProduct[] {
  return state.products.map((product) => {
    if (product.devOrderId !== devOrderId) return product;
    return {
      ...product,
      specPomLibraries: (product.specPomLibraries ?? []).map((item, index) =>
        rowKeyOf(item, index) === rowId ? updateItem(item) : item,
      ),
    };
  });
}

/** The edited POM row inside a freshly-built products array (for post-edit change detection). */
function findItem(products: MeasurementProduct[], devOrderId: string, rowKey: string): SpecPomItem | undefined {
  const product = products.find((entry) => entry.devOrderId === devOrderId);
  if (!product) return undefined;
  return (product.specPomLibraries ?? []).find((item, index) => rowKeyOf(item, index) === rowKey);
}

/**
 * Commit a single-row edit: enforce the 100-row cap, maintain `changedRowIds` (value-diffed against the
 * baseline so reverts drop out and tolerance/grading edits count), refresh the amber highlight state, and
 * clear any required-field error on a now-filled cell. Returns state unchanged (plus a cap warning) when
 * the edit would push a *new* row past the cap.
 */
function commitEdit(state: MeasurementsState, devOrderId: string, rowKey: string, nextProducts: MeasurementProduct[]): MeasurementsState {
  const globalId = rowGlobalId(devOrderId, rowKey);
  const original = getOriginalItem(state, devOrderId, rowKey);
  const editedItem = findItem(nextProducts, devOrderId, rowKey);
  const changedNow = isRowChanged(editedItem, original);
  const already = state.changedRowIds.includes(globalId);

  let {changedRowIds} = state;
  if (changedNow && !already) {
    if (state.changedRowIds.length >= EDIT_ROW_CAP) {
      /* Reject the edit — the user must save before touching another new row. */
      return { ...state, showEditCapWarning: true };
    }
    changedRowIds = [...state.changedRowIds, globalId];
  } else if (!changedNow && already) {
    changedRowIds = state.changedRowIds.filter((id) => id !== globalId);
  }

  /* Drop the required-field error for whichever of code/description is now non-empty. */
  const invalidCells = state.invalidCells.filter((cell) => {
    if (cell === `${globalId}::code`) return !String(editedItem?.code ?? '').trim();
    if (cell === `${globalId}::description`) return !String(editedItem?.description ?? '').trim();
    return true;
  });

  return { ...state, products: nextProducts, dirty: true, changedRowIds, invalidCells, highlightActive: hasHighlights(nextProducts) };
}

/** Exported for unit tests — the pure reducer + its initial state. */
export { initialState as measurementsInitialState };
export function measurementsReducer(state: MeasurementsState, action: MeasurementsAction): MeasurementsState {
  switch (action.type) {
    case 'VALIDATED':
      return {
        ...state,
        gradingGroupId: action.payload.commonGradingGroupId,
        eligibleProductIds: action.payload.eligibleProductIds,
        ineligibleProducts: action.payload.ineligibleProducts,
        phase:
          action.payload.ineligibleProducts.length > 0
            ? 'needs-confirm'
            : cohortPhase(action.payload.commonGradingGroupId, action.payload.eligibleProductIds),
      };
    case 'LOADING':
      return { ...state, phase: cohortPhase(state.gradingGroupId, state.eligibleProductIds) };
    case 'LOADED': {
      const { data, supportingDocs, gradingFormat } = action.payload;
      const stampedProducts = prepareProducts(data.products, unionSizeNames(data.sizeRanges, data.products), gradingFormat);
      return {
        ...state,
        phase: 'ready',
        gradingGroupId: data.gradingGroupId,
        gradingGroupName: data.gradingGroupName,
        conversionType: data.conversionType,
        gradingFormat,
        sizeRanges: data.sizeRanges,
        products: stampedProducts,
        originalProducts: cloneDeep(stampedProducts),
        supportingDocs,
        /* Products land expanded — every POM row of every product is visible without touching a chevron. */
        collapsedProducts: [],
        collapseAllActive: false,
        highlightActive: hasHighlights(stampedProducts),
        dirty: false,
        loadingMore: false,
        changedRowIds: [],
        invalidCells: [],
        showEditCapWarning: false,
        scrollToInvalid: null,
      };
    }
    case 'LOADING_MORE':
      return { ...state, loadingMore: true };
    case 'LOADED_MORE': {
      const { data, supportingDocs } = action.payload;
      /* Size columns span every page loaded so far, so a later page aligns against the whole cohort. */
      const sizeNames = unionSizeNames(state.sizeRanges, [...state.products, ...data.products]);
      const stampedProducts = prepareProducts(data.products, sizeNames, state.gradingFormat);
      const products = [...state.products, ...stampedProducts];
      /* Select-all is sticky: while it is on, a Load-More page arrives already ticked — parent rows only
         while a push is armed, every POM row otherwise — so scrolling never silently drops products out
         of the selection the user believes they made. */
      const pageRowIds = state.selectAllActive
        ? (isPushTargeting(state) ? parentRowIdsOf(stampedProducts, state.sourceDevOrderId) : pomRowIdsOf(stampedProducts))
        : [];
      const selectedRowIds = pageRowIds.length > 0 ? [...new Set([...state.selectedRowIds, ...pageRowIds])] : state.selectedRowIds;
      return {
        ...state,
        phase: 'ready',
        products,
        selectedRowIds,
        /* A scrolled-in page joining an un-armed bulk selection can widen it past one product, which has to
           drop the source the same way ticking that product by hand would. An armed source stays frozen. */
        sourceDevOrderId: state.pushArmed ? state.sourceDevOrderId : deriveSourceDevOrderId(selectedRowIds),
        /* Append to the pristine baseline so newly-loaded rows diff correctly on the next save. */
        originalProducts: [...state.originalProducts, ...cloneDeep(stampedProducts)],
        supportingDocs: { ...state.supportingDocs, ...supportingDocs },
        /* Collapse-all is sticky: while it is active a Load-More page arrives collapsed instead of expanded. */
        collapsedProducts: state.collapseAllActive
          ? [...state.collapsedProducts, ...stampedProducts.map((product) => product.devOrderId)]
          : state.collapsedProducts,
        highlightActive: state.highlightActive || hasHighlights(stampedProducts),
        loadingMore: false,
      };
    }
    case 'LOAD_MORE_ERROR':
      return { ...state, loadingMore: false };
    case 'ERROR':
      return { ...state, phase: 'error', error: action.payload };
    case 'COMMIT_CELL': {
      const { devOrderId, rowId, sizeName, field, value } = action.payload;
      const original = getOriginalItem(state, devOrderId, rowId);
      const products = updateProductItem(state, devOrderId, rowId, (item) => {
        const written: SpecPomItem = {
          ...item,
          gradings: (item.gradings ?? []).map((grading) => (grading.sizeName === sizeName ? { ...grading, [field]: value } : grading)),
        };
        return recalcRow(written, original, state.gradingFormat);
      });
      return commitEdit(state, devOrderId, rowId, products);
    }
    case 'SET_CODE_DESC': {
      const { devOrderId, rowId, field, value } = action.payload;
      const products = updateProductItem(state, devOrderId, rowId, (item) => ({ ...item, [field]: value }));
      return commitEdit(state, devOrderId, rowId, products);
    }
    case 'CHECK_DIFF': {
      const { devOrderId, rowId } = action.payload;
      const original = getOriginalItem(state, devOrderId, rowId);
      const products = updateProductItem(state, devOrderId, rowId, (item) => {
        const codeChanged = String(item.code ?? '').trim() !== String(original?.code ?? '').trim();
        const descChanged = String(item.description ?? '').trim() !== String(original?.description ?? '').trim();
        return {
          ...item,
          isUserCodeChanged: codeChanged,
          isSupplierCodeChanged: codeChanged,
          isUserDescChanged: descChanged,
          isSupplierDescChanged: descChanged,
        };
      });
      return { ...state, products, highlightActive: hasHighlights(products) };
    }
    case 'DOCS_REFRESHED':
      return { ...state, supportingDocs: action.payload.supportingDocs };
    case 'SET_TOLERANCE': {
      const { devOrderId, rowId, field, value } = action.payload;
      const products = updateProductItem(state, devOrderId, rowId, (item) => ({ ...item, [field]: value }));
      return commitEdit(state, devOrderId, rowId, products);
    }
    case 'TOGGLE_GRADING':
      return { ...state, activeGrading: !state.activeGrading };
    case 'CLEARED_HIGHLIGHTS':
      return {
        ...state,
        products: state.products.map((product) => ({
          ...product,
          specPomLibraries: (product.specPomLibraries ?? []).map(clearItemFlags),
        })),
        highlightActive: false,
      };
    case 'SAVED':
      /* Persisted — refresh the pristine baseline so the changed-row diff/cap restart from the saved state. */
      return { ...state, dirty: false, originalProducts: cloneDeep(state.products), changedRowIds: [], invalidCells: [], scrollToInvalid: null };
    case 'TOGGLE_COLLAPSE': {
      const collapsedProducts = toggleInList(state.collapsedProducts, action.payload.devOrderId);
      /* Expanding a single product ends collapse-all, so later Load-More pages go back to arriving expanded. */
      return { ...state, collapsedProducts, collapseAllActive: state.collapseAllActive && collapsedProducts.length === state.products.length };
    }
    case 'SET_ALL_COLLAPSED':
      return {
        ...state,
        collapsedProducts: action.payload.collapsed ? state.products.map((product) => product.devOrderId) : [],
        collapseAllActive: action.payload.collapsed,
      };
    case 'TOGGLE_SELECT': {
      const selectedRowIds = toggleInList(state.selectedRowIds, action.payload.rowId);
      /* Clearing the last selection returns to a clean slate. */
      if (selectedRowIds.length === 0) return { ...state, selectedRowIds, sourceDevOrderId: null, pushArmed: false, selectAllActive: false };
      /* Un-armed, the source follows the selection: it names a product only while the ticks stay inside
         one, so spreading across products switches Push off and unticking back down switches it on again.
         Armed, the source is frozen — the ticks arriving now are targets and must not rename it. */
      const sourceDevOrderId = state.pushArmed ? state.sourceDevOrderId : deriveSourceDevOrderId(selectedRowIds);
      return { ...state, selectedRowIds, sourceDevOrderId };
    }
    case 'SET_ALL_SELECTED': {
      const { selected } = action.payload;
      if (isPushTargeting(state)) {
        /* Armed: the header ticks every OTHER product's parent row, turning select-all into "push to all".
           The source keeps its own ticked rows — they are what the push copies from, so clearing them
           would silently disarm the interaction the user is halfway through. */
        const sourceRowIds = state.selectedRowIds.filter((rowId) => productIdOfRow(rowId) === state.sourceDevOrderId);
        const targetRowIds = selected ? parentRowIdsOf(state.products, state.sourceDevOrderId) : [];
        return { ...state, selectedRowIds: [...new Set([...sourceRowIds, ...targetRowIds])], selectAllActive: selected };
      }
      /* No push in progress: a plain bulk selection of every row, every tick green. Across a multi-product
         cohort that names no source and so leaves Push disabled; a lone product still names itself. */
      const selectedRowIds = selected ? [...new Set(allPomRowIds(state))] : [];
      return {
        ...state,
        selectedRowIds,
        sourceDevOrderId: deriveSourceDevOrderId(selectedRowIds),
        pushArmed: false,
        selectAllActive: selected,
      };
    }
    case 'TOGGLE_PUSH_ARM': {
      if (!state.pushArmed) return { ...state, pushArmed: true };
      /* Disarming returns to source-picking so the user can re-open the source's checkboxes. Target ticks
         are dropped: once disarmed the grid forbids selecting them, and leaving them behind would keep
         Push reading targets that are no longer changeable. */
      const sourceRowIds = state.selectedRowIds.filter((rowId) => productIdOfRow(rowId) === state.sourceDevOrderId);
      return { ...state, pushArmed: false, selectedRowIds: sourceRowIds, selectAllActive: false };
    }
    case 'RESET_PUSH':
      return { ...state, selectedRowIds: [], sourceDevOrderId: null, pushArmed: false, selectAllActive: false };
    case 'PUSH_MEASUREMENTS': {
      const source = state.products.find((product) => product.devOrderId === state.sourceDevOrderId);
      if (!source) return state;
      const sourceRows = selectedSourceRows(state, source);
      const targetIds = targetProductIds(state);
      if (sourceRows.length === 0 || targetIds.size === 0) return state;

      /* Push copies VALUES ONLY, into rows whose POM code matches exactly. The target keeps its own
         rows, codes, descriptions and tolerances; a code the target does not carry is skipped. */
      const changedRowIds = new Set(state.changedRowIds);
      const products = state.products.map((product) => {
        if (!targetIds.has(product.devOrderId)) return product;
        const specPomLibraries = (product.specPomLibraries ?? []).map((item, index) => {
          const match = sourceRows.find((sourceRow) => isSameCode(sourceRow.code, item.code));
          if (!match) return item;
          changedRowIds.add(rowGlobalId(product.devOrderId, rowKeyOf(item, index)));
          return { ...item, gradings: copyGradingValues(match.gradings ?? [], item.gradings ?? []) };
        });
        return { ...product, specPomLibraries };
      });

      /* Only matched rows count towards the cap, so a push rarely trips it. */
      if (changedRowIds.size > EDIT_ROW_CAP) return { ...state, showEditCapWarning: true };
      return {
        ...state,
        products,
        dirty: true,
        highlightActive: hasHighlights(products),
        changedRowIds: [...changedRowIds],
        /* A completed push clears the whole interaction — the button disables until a new source is picked. */
        selectedRowIds: [],
        sourceDevOrderId: null,
        pushArmed: false,
        selectAllActive: false,
      };
    }
    case 'SET_INVALID_CELLS':
      return { ...state, invalidCells: action.payload.invalidCells, scrollToInvalid: action.payload.scrollTo };
    case 'DISMISS_EDIT_CAP_WARNING':
      return { ...state, showEditCapWarning: false };
    case 'CLEAR_SCROLL_TARGET':
      return { ...state, scrollToInvalid: null };
    default:
      return state;
  }
}

const MeasurementsStateContext = createContext<MeasurementsState | undefined>(undefined);
const MeasurementsDispatchContext = createContext<React.Dispatch<MeasurementsAction> | undefined>(undefined);

const MeasurementsProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [state, dispatch] = useReducer(measurementsReducer, initialState);
  return (
    <MeasurementsStateContext.Provider value={state}>
      <MeasurementsDispatchContext.Provider value={dispatch}>{children}</MeasurementsDispatchContext.Provider>
    </MeasurementsStateContext.Provider>
  );
};

export const useMeasurementsState = (): MeasurementsState => {
  const context = useContext(MeasurementsStateContext);
  if (context === undefined) throw new Error('useMeasurementsState must be used within a MeasurementsProvider');
  return context;
};

export const useMeasurementsDispatch = (): React.Dispatch<MeasurementsAction> => {
  const context = useContext(MeasurementsDispatchContext);
  if (context === undefined) throw new Error('useMeasurementsDispatch must be used within a MeasurementsProvider');
  return context;
};

export default MeasurementsProvider;
